import { useState } from "react";
import {
  exportBackup,
  getBookshelfView,
  importBackup,
  type BookshelfViewItem,
  type ContentKind,
  type UserBackupSnapshot,
  type UserSpace,
} from "../api";
import { sortBookshelfView } from "../reader/reader-library";

interface BackupWorkspaceOptions {
  user: UserSpace | null;
  activeKind: ContentKind;
  setUser: (user: UserSpace) => void;
  setBookshelf: (items: BookshelfViewItem[]) => void;
  setBusy: (busy: boolean) => void;
  setMessage: (message: string) => void;
  refreshMonitor: (userId: string) => Promise<void>;
}

export function useBackupWorkspace({
  user,
  activeKind,
  setUser,
  setBookshelf,
  setBusy,
  setMessage,
  refreshMonitor,
}: BackupWorkspaceOptions) {
  const [backupText, setBackupText] = useState("");

  async function handleExportBackup() {
    if (!user) return;

    setBusy(true);
    setMessage("正在导出备份");

    try {
      const backup = await exportBackup(user.id);
      setBackupText(JSON.stringify(backup, null, 2));
      setMessage(`备份已导出，包含 ${backup.bookshelf.length} 条书架记录`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function handleImportBackup() {
    if (!backupText.trim()) return;

    setBusy(true);
    setMessage("正在恢复备份");

    try {
      const snapshot = JSON.parse(backupText) as UserBackupSnapshot;
      const restoredUser = await importBackup(snapshot);
      setUser(restoredUser);
      setBookshelf(sortBookshelfView(await getBookshelfView(restoredUser.id, activeKind)));
      await refreshMonitor(restoredUser.id);
      setMessage("备份已恢复");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return {
    backupText,
    setBackupText,
    handleExportBackup,
    handleImportBackup,
  };
}
